import { Graph, ModelStyle, ShapeStyle } from '@antv/g6';
import { ItemState } from 'ui/workflows/flowchart/constants';
import { Group, Node, Shape } from 'ui/workflows/flowchart/types';
import { select } from 'ui/workflows/flowchart/utils';
import { GroupName, shapeNames } from '../../../constants';
import { getWrapperStateShapeAttrs } from '../../base/baseUtils';

/**
 * Gets the style for a specific shape
 */
const getShapeStyle = ({
  shapeName,
  state,
  options,
  style,
}: {
  shapeName: string;
  state: ItemState;
  options: ModelStyle;
  style: ShapeStyle;
}): ShapeStyle => ({
  ...(shapeName === shapeNames.wrapper ? getWrapperStateShapeAttrs(state, options) : {}),
  ...style,
});

/**
 * Updates a shape with new styles
 */
const updateShapeStyle = ({ shape, style }: { shape: Shape; style: ShapeStyle }) =>
  Object.entries(style).forEach(([key, value]) => shape.attr(key, value));

/**
 * Gets what the new state array will look like with the state added/removed
 * NOTE: If the state exists, it will be moved to the end of the array so that
 * it maintains priority
 */
const getUpdatedStates = (states: ItemState[], state: ItemState, stateValue = true) => {
  const stateIdx = states.indexOf(state);
  if (stateIdx > -1) {
    states.splice(stateIdx, 1);
  }
  if (stateValue) {
    states.push(state);
  }
  return states;
};

const getStateStyle = (state: ItemState, options: ModelStyle) => {
  return options?.stateStyles?.[state] || ({} as Record<string, ShapeStyle>);
};

/**
 * Gets the style if the state was not enabled
 * Important for when turning the state off
 */
const getBaseStyle = (state: ItemState, options: ModelStyle) =>
  Object.entries(getStateStyle(state, options)).reduce((acc, [shapeName, style]) => {
    const baseStyle = (options[shapeName] || {}) as ShapeStyle;
    const stateStyle = getShapeStyle({ shapeName, style, options, state });
    const baseProps = Object.keys(stateStyle).reduce((a, k) => ({ ...a, [k]: baseStyle[k] }), {});

    return { ...acc, [shapeName]: baseProps };
  }, {});

/**
 * Gets the styles for all the current states
 */
const getStyles = ({
  states,
  options,
  baseStyle = {},
}: {
  states: ItemState[];
  options: ModelStyle;
  baseStyle: ModelStyle;
}) =>
  states.reduce(
    (acc: ModelStyle, state) =>
      Object.entries(getStateStyle(state, options)).reduce(
        (styleAcc: ModelStyle, [shapeName, style]) => ({
          ...styleAcc,
          // For each shape type within each state style, override the options
          [shapeName]: {
            ...((styleAcc[shapeName] || {}) as ShapeStyle),
            ...getShapeStyle({ shapeName, style, options, state }),
          },
        }),
        acc,
      ),
    baseStyle,
  );

/**
 * Updates the child node styling as if a state has been applied
 */
export const setChildNodeState = (childNode: Group, updatedState: ItemState, stateValue = true) => {
  if (childNode?.get('name') !== GroupName.ChildNode) return;

  const states = getUpdatedStates(childNode.get('states') || [], updatedState, stateValue) || [];
  const options = childNode.get('options');
  const styles = getStyles({ states, options, baseStyle: getBaseStyle(updatedState, options) });

  Object.entries(styles as Record<string, ShapeStyle>).forEach(([shapeName, style]) => {
    childNode.findAllByName(shapeName).forEach((shape) => updateShapeStyle({ shape, style }));
  });

  childNode.set('states', states);
};

/**
 * Selects a child node
 *
 * This operates under the assumption that:
 *  - Within the entire graph, only 1 child node can be selected at a time
 *     - This means that all other nodes will have their child nodes deselected
 *  - When a child node is selected, its parent node is selected.
 *    - This is important for things like:
 *      - moving the node in front of the overlay
 *      - delete/copy actions
 *      - ...and more
 */
export const selectChildNode = ({
  node,
  graph,
  index = -1,
}: {
  node: Node | string;
  graph: Graph;
  index?: number;
}) => {
  const nodeId = typeof node === 'string' ? node : node.getID();

  graph.getNodes().forEach((item) => {
    const childNodes = item.getContainer().findAllByName(GroupName.ChildNode) as Group[];

    if (childNodes?.length) {
      const isSelectedNode = item.getID() === nodeId;

      // If the node is not selected, select it.
      if (isSelectedNode && !item.getStates().includes(ItemState.Selected)) {
        select(graph, item);
      }

      childNodes.forEach((childNode, cIndex) => {
        setChildNodeState(childNode, ItemState.Selected, isSelectedNode && cIndex === index);
      });
    }
  });
};

/**
 * Removes selected state on child nodes if they are not eligible
 */
export const updateSelectedChildNodes = (graph?: Graph | null) =>
  graph &&
  graph.getNodes().forEach((node) => {
    const childNodes = node.getContainer().findAllByName(GroupName.ChildNode) as Group[];

    if (childNodes?.length && !node.getStates().includes(ItemState.Selected)) {
      childNodes.forEach((childNode) => setChildNodeState(childNode, ItemState.Selected, false));
    }
  });
